// Export Excel (.xlsx) — approche « ce que tu vois » : on lit les tableaux HTML
// rendus et on les convertit en feuilles. Un onglet Palestre = une feuille Excel
// (les tableaux multiples d'un onglet sont empilés dans la même feuille).
//
// Convention suisse (cf. format.js) : milliers = apostrophe, décimal = point.
// Le séparateur de milliers du DOM peut être l'apostrophe ASCII (') ou
// typographique (’ U+2019) selon le moteur Intl — on neutralise les deux.
//
// Format de nombre Excel : `#,##0` (JAMAIS `#'##0` — l'apostrophe littérale
// casserait l'affichage ; Excel applique le séparateur selon la locale système).

/* EXCELJS NE SE CHARGE QU'AU MOMENT DE L'EXPORT (2026-09-18). Il pesait
   921 ko des 4,6 Mo du paquet servi à l'ouverture de l'application, pour un
   bouton qu'on clique quelques fois par mois : tout le monde payait le prix
   d'un classeur Excel, y compris en ouvrant l'Accueil. Le module reste en
   mémoire après le premier export, le second part donc sans attente. */
let excelJs = null;
function loadExcelJs() {
  if (!excelJs) excelJs = import('exceljs').then((m) => m.default || m);
  return excelJs;
}

// Charte graphique (ARGB = FF + hex)
const HEADER_FILL = 'FFE9EDF4'; // en-tête de card bleu-gris
const HEADER_BORDER = 'FFDCE2EC';
const TOTAL_FILL = 'FFE5EBF4'; // ligne total bleu-gris
const TOTAL_BORDER = 'FFC7D2E2';
const TITLE_COLOR = 'FF2B203E'; // navy

const EMPTY_TOKENS = new Set(['', '—', '–', '-', 'n/a', 'N/A']);

/**
 * Parse un nombre affiché en format suisse. Retourne { value, decimals, percent }
 * ou null si la chaîne n'est pas numérique.
 */
export function parseSwissNumber(raw) {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (EMPTY_TOKENS.has(s)) return null;
  const percent = /%/.test(s);
  // Retire CHF / kCHF / % et tout espace (normal, insécable, fin)
  s = s.replace(/kCHF|CHF|%/gi, '').replace(/\s/g, '').trim();
  // Signe (préfixe - − ou parenthèses comptables)
  const neg = /^[-−]/.test(s) || /^\(.*\)$/.test(s);
  s = s.replace(/[()]/g, '').replace(/^[-−+]/, '').trim();
  // Sépare milliers (apostrophe ASCII ' + typographique ’)
  s = s.replace(/['’]/g, '');
  // Si virgule décimale (rare ici), normalise en point
  if (/,/.test(s) && !/\./.test(s)) s = s.replace(',', '.');
  if (s === '' || !/^\d*\.?\d+$/.test(s)) return null;
  let value = Number(s);
  if (Number.isNaN(value)) return null;
  if (neg) value = -value;
  const dot = s.indexOf('.');
  const decimals = dot === -1 ? 0 : s.length - dot - 1;
  return { value, decimals, percent };
}

function numFmtFor({ decimals, percent }) {
  const frac = decimals > 0 ? '.' + '0'.repeat(decimals) : '';
  if (percent) return `0${frac}" %"`;
  return `#,##0${frac}`;
}

/* Contenu MASQUÉ d'un élément : infobulle révélée au survol, bloc « print only »,
   variante d'affichage repliée. Il n'est pas à l'écran, il n'a rien à faire dans
   la feuille. Sans ce filtre, le bilan prévisionnel sortait ses cellules sous la
   forme « 277'861 +5'041Liquidité — équilibre du bilan (cash-plug) » : le chiffre,
   la variation et l'origine collés, donc plus un nombre mais du texte.

   Le style calculé n'est interrogé que si l'élément porte une classe ou un style
   en propre — sinon l'appel, coûteux, se répéterait sur chaque <b> ou <span> nu
   d'un classeur de plusieurs milliers de cellules. */
function isNodeHidden(el) {
  if (/is-no-export/.test(el.className || '')) return true;
  if (!el.className && !el.hasAttribute?.('style')) return false;
  const win = el.ownerDocument && el.ownerDocument.defaultView;
  if (!win || !win.getComputedStyle) return false;
  const st = win.getComputedStyle(el);
  return !!st && (st.display === 'none' || st.visibility === 'hidden');
}

// Texte VISIBLE d'un nœud, enfants séparés d'une espace (évite de coller deux
// <span>, ex. « 100% (CHF/m) » + « Salaire brut »).
function visibleText(node) {
  if (node.nodeType === 3) return node.nodeValue || '';        // nœud texte
  if (node.nodeType !== 1) return '';                          // commentaire…
  if (isNodeHidden(node)) return '';
  let out = '';
  node.childNodes.forEach((n) => { out += visibleText(n) + ' '; });
  return out;
}

// Texte « propre » d'une cellule : valeur d'un champ de saisie si présent,
// sinon le texte visible, indicateurs de tri ôtés.
function cellText(cell) {
  // Valeur d'export explicite (cellule au rendu visuel peu lisible, ex. puces de mois)
  if (cell && cell.getAttribute) {
    const ev = cell.getAttribute('data-export-value');
    if (ev != null) return ev;
  }
  const field = cell.querySelector('input, select, textarea');
  if (field) {
    // Cases à cocher / radios = sélection UI (value « on ») → pas une donnée
    if (field.type === 'checkbox' || field.type === 'radio') return '';
    return (field.value ?? '').trim();
  }
  let t = '';
  cell.childNodes.forEach((n) => { t += visibleText(n) + ' '; });
  return t.replace(/[↑↓↕]/g, '').replace(/\s+/g, ' ').trim();
}

// Cellule cachée à l'écran (ex. doublon « print only » en display:none) → exclue,
// pour rester fidèle à l'affichage et ne pas décaler les colonnes.
function isCellHidden(td) {
  const win = td.ownerDocument && td.ownerDocument.defaultView;
  if (!win || !win.getComputedStyle) return false;
  const st = win.getComputedStyle(td);
  return !!st && (st.display === 'none' || st.visibility === 'hidden');
}

// Développe une ligne en colonnes en honorant colSpan (rowSpan ignoré — non
// utilisé dans nos grilles). Retourne [{ cell, text }] aligné par colonne.
function expandRow(tr) {
  const out = [];
  for (const td of tr.cells) {
    if (isCellHidden(td)) continue;
    const span = td.colSpan || 1;
    out.push({ cell: td, text: cellText(td) });
    for (let k = 1; k < span; k++) out.push({ cell: null, text: '' });
  }
  return out;
}

function toCell(text) {
  const num = parseSwissNumber(text);
  if (num) return { v: num.value, fmt: numFmtFor(num) };
  return { v: EMPTY_TOKENS.has(text) ? null : text };
}

const hasControl = (cell) => !!(cell && cell.querySelector('button, select, input, textarea'));

/** Convertit un <table> en bloc { title, header, rows, totals } (colonnes alignées). */
export function tableToBlock(table) {
  const title = table.getAttribute('data-export-name') || null;
  const theadRows = table.tHead ? [...table.tHead.rows] : [];
  const headRow = theadRows.length ? theadRows[theadRows.length - 1] : null;
  const headCols = headRow ? expandRow(headRow) : [];

  // Lignes du corps (hors is-no-export). Les lignes de section (une cellule en
  // colSpan, ex. « Charges salarié ») sont CONSERVÉES comme libellés de section.
  const bodyTrs = [];
  for (const tb of table.tBodies) {
    for (const tr of tb.rows) {
      if (/is-no-export/.test(tr.className)) continue;
      bodyTrs.push(tr);
    }
  }
  const footTrs = table.tFoot ? [...table.tFoot.rows] : [];
  const bodyCols = bodyTrs.map(expandRow);
  const footCols = footTrs.map(expandRow);

  const ncol = Math.max(
    headCols.length,
    ...bodyCols.map((r) => r.length),
    ...footCols.map((r) => r.length),
    0,
  );

  // Colonne ignorée seulement si : en-tête marqué no-print/is-no-export, OU
  // c'est une colonne d'ACTIONS (en-tête vide + toutes les cellules du corps
  // vides ou ne contenant qu'un contrôle bouton/champ). Une colonne de libellés
  // à en-tête vide (coin haut-gauche) est conservée.
  const keep = [];
  for (let i = 0; i < ncol; i++) {
    const h = headCols[i];
    const headerCls = h && h.cell ? (h.cell.className || '') : '';
    if (/hr-page__no-print|is-no-export/.test(headerCls)) { keep[i] = false; continue; }
    const headerText = h ? h.text : '';
    if (headerText !== '') { keep[i] = true; continue; }
    // En-tête vide : garder sauf si colonne d'actions (cellules vides ou contrôle seul)
    let hasBody = false;
    let allControl = true;
    for (const r of bodyCols) {
      const c = r[i];
      if (!c) continue;
      if (c.text === '') continue;            // cellule vide → neutre
      hasBody = true;
      if (!hasControl(c.cell)) { allControl = false; break; }
    }
    keep[i] = hasBody ? !allControl : false;  // pas de contenu du tout → colonne vide → ignorée
  }

  const filterRow = (cols) => {
    const padded = cols.slice();
    while (padded.length < ncol) padded.push({ cell: null, text: '' });
    return padded.filter((_, i) => keep[i]).map((c) => toCell(c.text));
  };

  const header = headCols.length
    ? headCols.slice().concat(Array(Math.max(0, ncol - headCols.length)).fill({ text: '' }))
        .filter((_, i) => keep[i]).map((h) => h.text)
    : [];
  const rows = bodyCols.map(filterRow);
  const totals = footCols.map(filterRow);

  return { title, header, rows, totals };
}

function styleRow(row, { fill, border, bold = true }) {
  row.eachCell((cell) => {
    cell.font = { bold, name: 'Inter', size: 10 };
    if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
    if (border) cell.border = border;
  });
}

function writeBodyRow(ws, cells) {
  const row = ws.addRow(cells.map((c) => (c.v == null ? null : c.v)));
  cells.forEach((c, i) => {
    if (c.fmt) {
      const cell = row.getCell(i + 1);
      cell.numFmt = c.fmt;
      cell.alignment = { horizontal: 'right' };
    }
  });
  return row;
}

function autoWidth(ws) {
  ws.columns.forEach((col) => {
    let max = 8;
    col.eachCell({ includeEmpty: false }, (cell) => {
      const len = cell.value == null ? 0 : String(cell.value).length;
      if (len > max) max = len;
    });
    col.width = Math.min(max + 2, 48);
  });
}

function safeSheetName(name, index, used) {
  let base = (name || `Onglet ${index + 1}`).replace(/[\\/?*[\]:]/g, ' ').trim().slice(0, 31);
  if (!base) base = `Onglet ${index + 1}`;
  let n = base;
  let k = 2;
  while (used.has(n)) { n = `${base.slice(0, 28)} ${k}`; k += 1; }
  used.add(n);
  return n;
}

/** Construit + télécharge un classeur depuis des feuilles { name, blocks:[{title,header,rows,totals}] }. */
export async function downloadWorkbook(filename, sheets) {
  const ExcelJS = await loadExcelJs();
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Palestre';
  const used = new Set();
  sheets.forEach((sheet, idx) => {
    const ws = wb.addWorksheet(safeSheetName(sheet.name, idx, used));
    let firstHeaderRow = null;
    sheet.blocks.forEach((block, bi) => {
      if (bi > 0) ws.addRow([]); // ligne vide entre tables
      if (block.title) {
        const tr = ws.addRow([block.title]);
        tr.getCell(1).font = { bold: true, name: 'Schibsted Grotesk', size: 12, color: { argb: TITLE_COLOR } };
      }
      if (block.header && block.header.length) {
        const hr = ws.addRow(block.header);
        styleRow(hr, { fill: HEADER_FILL, border: { bottom: { style: 'thin', color: { argb: HEADER_BORDER } } } });
        if (firstHeaderRow == null) firstHeaderRow = hr.number;
      }
      (block.rows || []).forEach((r) => writeBodyRow(ws, r));
      (block.totals || []).forEach((r) => styleRow(writeBodyRow(ws, r), { fill: TOTAL_FILL, border: { top: { style: 'thin', color: { argb: TOTAL_BORDER } } } }));
    });
    if (firstHeaderRow != null) ws.views = [{ state: 'frozen', ySplit: firstHeaderRow }];
    autoWidth(ws);
  });

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const slug = (s) => (s || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

/**
 * Construit le classeur d'un hub : un onglet (élément [data-export-tab]) =
 * une feuille ; les <table> de l'onglet (hors is-no-export) y sont empilés.
 * Retourne le nombre de feuilles écrites (0 = aucune donnée).
 *
 * `sheetName` sert les SECTIONS SANS ONGLET : le conteneur ne porte alors aucun
 * [data-export-tab], et c'est lui-même qui fait la feuille (une seule).
 */
export async function buildHubWorkbook(stageEl, { fileBase, tenantName, year, sheetName } = {}) {
  if (!stageEl) return 0;
  const win = stageEl.ownerDocument?.defaultView;
  // Table cachée à l'écran (ex. blocs « print only » en display:none) → exclue :
  // on remonte les ancêtres jusqu'au conteneur d'export.
  const isHidden = (el) => {
    if (!win || !win.getComputedStyle) return false;
    let n = el;
    while (n && n !== stageEl) {
      const st = win.getComputedStyle(n);
      if (st && (st.display === 'none' || st.visibility === 'hidden')) return true;
      n = n.parentElement;
    }
    return false;
  };
  const marked = [...stageEl.querySelectorAll('[data-export-tab]')]
    .map((el) => ({ el, label: el.getAttribute('data-export-tab') }));
  // Aucun onglet marqué = section sans onglet : le conteneur EST la feuille.
  const targets = marked.length ? marked : (sheetName ? [{ el: stageEl, label: sheetName }] : []);
  const sheets = [];
  for (const { el, label } of targets) {
    const tables = [...el.querySelectorAll('table')].filter(
      (t) => !/is-no-export/.test(t.className) && !t.closest('.is-no-export') && !isHidden(t),
    );
    if (!tables.length) continue;
    // Une table marquée data-export-sheet = feuille à part (nom = data-export-name).
    // Les autres tables de l'onglet sont empilées dans une feuille au nom de l'onglet.
    const ownSheet = tables.filter((t) => t.hasAttribute('data-export-sheet'));
    const stacked = tables.filter((t) => !t.hasAttribute('data-export-sheet'));
    for (const t of ownSheet) {
      sheets.push({ name: t.getAttribute('data-export-name') || label, blocks: [tableToBlock(t)] });
    }
    if (stacked.length) sheets.push({ name: label, blocks: stacked.map(tableToBlock) });
  }
  if (!sheets.length) return 0;
  const parts = ['palestre', slug(fileBase), slug(tenantName), year].filter(Boolean);
  await downloadWorkbook(parts.join('_'), sheets);
  return sheets.length;
}
